import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Авто-редирект при истечении сессии. Игнорируем auth-endpoints (их ошибки
// обрабатываются на форме логина/сброса), чтобы не войти в цикл.
const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/forgot-password',
                    '/auth/set-password', '/auth/me'];
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = (err?.config?.url || '').toString();
    const isAuthPath = AUTH_PATHS.some(p => url.endsWith(p));
    if (status === 401 && !isAuthPath) {
      const loc = window.location.pathname;
      if (loc !== '/' && loc !== '/login' && !loc.startsWith('/set-password')) {
        // Сообщаем один раз и редиректим на логин.
        if (!window.__bq_session_expired) {
          window.__bq_session_expired = true;
          try { window.dispatchEvent(new CustomEvent('bq:session-expired')); } catch (_e) { /* noop */ }
          setTimeout(() => { window.__bq_session_expired = false; }, 3000);
        }
        window.location.href = '/';
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (!detail) return 'Произошла ошибка. Попробуйте ещё раз.';
  if (typeof detail === 'string') return translateError(detail);
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === 'string' ? translateError(e.msg) : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return translateError(detail.msg);
  return String(detail);
}

/**
 * Форматирует ошибку axios с учётом сети/таймаута/CORS.
 * Принимает целиком объект ошибки axios и возвращает читаемое сообщение.
 * Используется вместо formatApiError(err.response?.data?.detail) везде,
 * где нужно различать «бэкенд ответил ошибкой» и «запрос не дошёл».
 */
export function formatAxiosError(err) {
  // Диагностика в консоль браузера — увидит и пользователь, и техподдержка.
  try {
    console.error('[BaseQuote API error]', {
      message: err?.message,
      code: err?.code,
      url: err?.config?.url,
      method: err?.config?.method,
      status: err?.response?.status,
      data: err?.response?.data,
    });
  } catch (_e) { /* ignore logging failures */ }
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail) return formatApiError(detail);
  // Есть ответ, но без detail — покажем статус.
  if (status) {
    if (status === 500) return 'Ошибка сервера (500). Мы уже знаем — попробуйте позже.';
    if (status === 502 || status === 503 || status === 504)
      return `Сервер временно недоступен (${status}). Попробуйте через минуту.`;
    if (status === 401) return 'Неверный email или пароль';
    if (status === 403) return 'Доступ запрещён';
    if (status === 404) return 'Ресурс не найден (404)';
    return `Ошибка ${status}. Попробуйте ещё раз.`;
  }
  // Ответа нет вообще — сеть / CORS / таймаут / оффлайн.
  if (err?.code === 'ECONNABORTED') return 'Таймаут запроса. Проверьте интернет и попробуйте ещё раз.';
  if (typeof navigator !== 'undefined' && navigator.onLine === false)
    return 'Нет соединения с интернетом. Проверьте сеть.';
  // Классический CORS / Network Error от axios/browser.
  const msg = (err?.message || '').toLowerCase();
  if (msg.includes('network')) {
    return 'Не удаётся связаться с сервером. Возможные причины: блокировка сети, VPN/прокси, антивирус или расширение браузера. Попробуйте в режиме инкогнито или отключите расширения.';
  }
  return `Не удалось выполнить запрос: ${err?.message || 'неизвестная ошибка'}.`;
}

// Текущая локаль участника (устанавливается AuthContext при логине/обновлении).
// Для ENG-пользователей переводим известные Russian-сообщения с бэкенда.
let _currentLang = 'ru';
export function setCurrentLang(lang) { _currentLang = lang === 'en' ? 'en' : 'ru'; }

// Точные соответствия Russian → English для стандартных ошибок бэкенда.
const ERROR_MAP_RU_EN = {
  'Произошла ошибка. Попробуйте ещё раз.': 'Something went wrong. Please try again.',
  'Неверный email или пароль': 'Invalid email or password',
  'Пользователь с таким email не зарегистрирован': 'No user registered with this email',
  'Неверный пароль. Проверьте раскладку клавиатуры и регистр, либо воспользуйтесь «Забыли пароль?».':
    'Incorrect password. Check your keyboard layout and case, or use "Forgot password?".',
  'Пароль ещё не установлен. Воспользуйтесь ссылкой из письма-приглашения или запросите её у супер-администратора.':
    'Password not yet set. Please use the invitation link from your email, or request one from the super-administrator.',
  'Введите email и пароль': 'Please enter email and password',
  'Неверный формат email': 'Invalid email format',
  'Пароль должен быть не короче 6 символов': 'Password must be at least 6 characters long',
  'Новый пароль должен быть не короче 6 символов': 'New password must be at least 6 characters long',
  'Email уже зарегистрирован. Используйте вход или восстановление пароля.':
    'Email is already registered. Please log in or reset your password.',
  'Неверный секретный код': 'Invalid secret code',
  "Неверный секретный код для направления 'Все'": "Invalid secret code for 'All' direction",
  'Не авторизован': 'Not authorized',
  'Сессия истекла': 'Session expired',
  'Токен истёк': 'Token expired',
  'Неверный токен': 'Invalid token',
  'Пользователь не найден': 'User not found',
  'Тендер не найден': 'Tender not found',
  'Доступ запрещён': 'Access denied',
  'Требуются права супер-администратора': 'Super-administrator rights required',
  'Укажите текущий пароль для смены': 'Please provide current password to change it',
  'Текущий пароль неверен': 'Current password is incorrect',
  'Ссылка устарела или уже была использована. Запросите новую через «Забыли пароль?».':
    'Link expired or already used. Please request a new one via "Forgot password?".',
  'Срок действия ссылки истёк. Запросите новую через «Забыли пароль?».':
    'Link has expired. Please request a new one via "Forgot password?".',
  'Email уже используется другим пользователем': 'Email is already used by another user',
  'Ваша заявка на участие ещё не принята организатором': 'Your participation application has not been accepted by the organizer yet',
  'Этап сбора заявок завершён': 'Application collection phase has ended',
  'Только участники могут подавать заявки': 'Only participants can submit applications',
  'Заявка не найдена': 'Application not found',
  'Неверный статус': 'Invalid status',
  'Товар с таким названием уже существует': 'A product with this name already exists',
  'Товар не найден': 'Product not found',
  'Название не может быть пустым': 'Name cannot be empty',
  'Нельзя удалить себя': 'You cannot delete yourself',
  'Нельзя удалить супер-администратора': 'Cannot delete super-administrator',
};

function translateError(msg) {
  if (_currentLang === 'en') {
    const exact = ERROR_MAP_RU_EN[msg];
    if (exact) return exact;
    // Fallback для "Произошла ошибка..." дефолтного сообщения.
    if (msg === 'Произошла ошибка. Попробуйте ещё раз.') return ERROR_MAP_RU_EN[msg];
  }
  return msg;
}

export function formatDate(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch {
    return isoString;
  }
}

/**
 * Форматировать дату с конвертацией в таймзону пользователя
 * @param {string} isoString - дата в ISO (МСК +03:00)
 * @param {object} userTz - {iana: "Asia/Shanghai", offset_hours: 8, label: "CST"}
 * @param {boolean} showLabel - показывать метку таймзоны
 */
export function formatDateLocal(isoString, userTz, showLabel = true) {
  if (!isoString) return '';
  try {
    const iana = userTz?.iana || 'Europe/Moscow';
    const label = userTz?.label || 'МСК';
    const d = new Date(isoString);
    // Если строка без timezone — считаем МСК (+03:00)
    if (!isoString.includes('+') && !isoString.includes('Z') && isoString.length > 10) {
      // Нет offset — это МСК, сдвигаем из локального в UTC для корректного Date
      const mskDate = new Date(isoString + '+03:00');
      const formatted = mskDate.toLocaleString('ru-RU', {
        timeZone: iana,
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false
      });
      return showLabel ? `${formatted} (${label})` : formatted;
    }
    const formatted = d.toLocaleString('ru-RU', {
      timeZone: iana,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    return showLabel ? `${formatted} (${label})` : formatted;
  } catch {
    return isoString;
  }
}

/**
 * Форматировать дату как МСК (для организатора)
 */
export function formatDateMSK(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    if (!isoString.includes('+') && !isoString.includes('Z') && isoString.length > 10) {
      const mskDate = new Date(isoString + '+03:00');
      const formatted = mskDate.toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false
      });
      return `${formatted} МСК`;
    }
    const formatted = d.toLocaleString('ru-RU', {
      timeZone: 'Europe/Moscow',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    return `${formatted} МСК`;
  } catch {
    return isoString;
  }
}

export function formatCurrency(value, currency = 'USD') {
  if (value == null) return '-';
  return `${Number(value).toFixed(2)} ${currency}`;
}

// Математический движок (клиентская версия) — должен совпадать с backend/math_engine.py
// Логистическая надбавка: U = C / W
// Стоимость доставки 1 контейнера делим на вес упаковки поставщика → стоимость
// доставки 1 ед. товара. Не зависит ни от цены товара, ни от объёма лота.
export function calcLogisticsU(volume, pricePerTon, weightInContainer, logisticsCostPerContainer) {
  if (logisticsCostPerContainer <= 0 || weightInContainer <= 0) return 0;
  const U = logisticsCostPerContainer / weightInContainer;
  return Math.round(U * 100) / 100;
}

export function calcMoneyCostE(pricePerTon, prepayPercent, prepayDays, delayPercent, delayDays, moneyCostDay) {
  // Предоплата = издержка для покупателя (увеличивает приведённую цену)
  // Отсрочка = выгода для покупателя (уменьшает приведённую цену)
  // E = E_prepay - E_delay
  if (moneyCostDay <= 0) return 0;
  const D = moneyCostDay / 100;
  const E_prepay = (pricePerTon * (prepayPercent / 100) * D) * prepayDays;
  const E_delay = (pricePerTon * (delayPercent / 100) * D) * delayDays;
  return Math.round((E_prepay - E_delay) * 100) / 100;
}

export function calcAdjustedPrice(price, U, E) {
  return Math.round((price + U + E) * 100) / 100;
}

/**
 * 1C ERP расчёт приведённой цены для импортного лота.
 * Полное зеркало backend/math_engine.py::compute_bid_indicators (ветка is_import=True).
 *
 * Возвращает { U, E, adjusted, bankFee, customsPerUnit } — все в валюте лота на единицу.
 *
 * tender: должен содержать is_import, volume, duty_rate, vat_rate, customs_fee, money_cost_day,
 *         supplier_freight, supplier_post_customs, supplier_transit_days (объекты email→число).
 * email:  email текущего поставщика (lowercase будет сделано здесь).
 * bidPrice, prepayPct, prepayDays, delayPct, delayDays — параметры ставки.
 */
export function calcAdjusted1cErp(tender, email, bidPrice, prepayPct, prepayDays, delayPct, delayDays, weightInContainer) {
  const A = parseFloat(bidPrice) || 0;
  // Вес партии — то, что указал поставщик в своей ставке (weight_in_container).
  // Fallback на tender.volume (общий объём лота) если поставщик ещё не ввёл вес.
  const W = parseFloat(weightInContainer) || parseFloat(tender?.volume) || 1;
  const V = W;
  const lowerEmail = (email || '').toLowerCase().trim();
  const norm = (src) => {
    if (!src) return {};
    const o = {};
    Object.entries(src).forEach(([k, v]) => { o[(k || '').toLowerCase().trim()] = v; });
    return o;
  };
  const freight = parseFloat(norm(tender?.supplier_freight)[lowerEmail]) || 0;
  const post = parseFloat(norm(tender?.supplier_post_customs)[lowerEmail]) || 0;
  const transit = parseInt(norm(tender?.supplier_transit_days)[lowerEmail]) || 0;
  const duty = (parseFloat(tender?.duty_rate) || 0) / 100;
  const vat = (parseFloat(tender?.vat_rate) || 0) / 100;
  const customsFee = parseFloat(tender?.customs_fee) || 0;
  const moneyDay = (parseFloat(tender?.money_cost_day) || 0) / 100;
  const pp = (parseFloat(prepayPct) || 0) / 100;
  const pd = parseFloat(prepayDays) || 0;
  const dp = (parseFloat(delayPct) || 0) / 100;
  const dd = parseFloat(delayDays) || 0;

  const bankFee = A * 0.03;
  const U = (freight + post) / V;
  const dynValue = A * V + freight;
  const dutyAmount = dynValue * duty;
  const vatAmount = (dynValue + dutyAmount) * vat;
  const totalCustoms = dutyAmount + vatAmount + customsFee;
  const customsPerUnit = totalCustoms / V;
  const daysFrozen = pp * (pd + transit) + dp * (transit - dd);
  const baseE = A * daysFrozen * moneyDay;
  const E = baseE * (1 + vat);
  const adjusted = A + U + E + customsPerUnit + bankFee;

  const r = (x) => Math.round(x * 100) / 100;
  return {
    U: r(U),
    E: r(E),
    bankFee: r(bankFee),
    customsPerUnit: r(customsPerUnit),
    adjusted: r(adjusted),
  };
}

export default api;
